import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PlatformMetrics from './components/PlatformMetrics';
import RevenueChart from './components/RevenueChart';
import UserManagementTable from './components/UserManagementTable';
import SystemConfiguration from './components/SystemConfiguration';
import QuickActions from './components/QuickActions';
import AuditLogViewer from './components/AuditLogViewer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useTheme } from '../../contexts/ThemeContext';

const AdminPortalDashboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { toggleTheme, isDark } = useTheme();

  const user = {
    name: "Admin User",
    role: "Platform Administrator"
  };

  const handleLogout = () => {
    // Clear authentication state
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    navigate('/login');
    setIsUserMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/home');
  };

  const handleThemeToggle = () => {
    toggleTheme();
    setIsUserMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsUserMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const navigationSections = [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'BarChart3',
      description: 'Platform metrics and analytics'
    },
    {
      id: 'events',
      label: 'Events',
      icon: 'Calendar',
      description: 'Event management and oversight'
    },
    {
      id: 'users',
      label: 'User Management',
      icon: 'Users',
      description: 'Manage platform users'
    },
    {
      id: 'audit',
      label: 'Audit Logs',
      icon: 'FileText',
      description: 'System activity tracking'
    },
    {
      id: 'config',
      label: 'Configuration',
      icon: 'Settings',
      description: 'System settings'
    },
    {
      id: 'actions',
      label: 'Quick Actions',
      icon: 'Zap',
      description: 'Administrative tasks'
    }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-8">
            <PlatformMetrics />
            <RevenueChart />
          </div>
        );
      case 'events':
        return (
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Event Management</h3>
            <p className="text-muted-foreground mb-6">Manage and oversee all platform events.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Button
                onClick={() => navigate('/admin/events')}
                className="flex flex-col items-center p-6 h-auto"
                variant="outline"
              >
                <Icon name="Calendar" size={24} className="mb-2" />
                <span className="font-medium">All Events</span>
                <span className="text-sm text-muted-foreground">View all events</span>
              </Button>
              <Button
                onClick={() => navigate('/venue-management')}
                className="flex flex-col items-center p-6 h-auto"
                variant="outline"
              >
                <Icon name="MapPin" size={24} className="mb-2" />
                <span className="font-medium">Venues</span>
                <span className="text-sm text-muted-foreground">Manage venues</span>
              </Button>
              <Button
                onClick={() => navigate('/merchandise-management')}
                className="flex flex-col items-center p-6 h-auto"
                variant="outline"
              >
                <Icon name="Package" size={24} className="mb-2" />
                <span className="font-medium">Merchandise</span>
                <span className="text-sm text-muted-foreground">Product management</span>
              </Button>
            </div>
          </div>
        );
      case 'users':
        return <UserManagementTable />;
      case 'audit':
        return <AuditLogViewer />;
      case 'config':
        return <SystemConfiguration />;
      case 'actions':
        return <QuickActions />;
      default:
        return (
          <div className="space-y-8">
            <PlatformMetrics />
            <RevenueChart />
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Logo and Profile */}
      <header className="bg-card border-b border-border shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button 
              onClick={handleLogoClick}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Ticket" size={20} color="white" />
              </div>
              <span className="text-lg font-semibold text-foreground">Tixful</span>
            </button>

            {/* User Profile */}
            <div className="flex items-center space-x-4">
              <div className="hidden sm:flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Clock" size={16} />
                <span>Last updated: {new Date()?.toLocaleTimeString()}</span>
              </div>
              
              {/* User Profile Dropdown */}
              <div className="relative">
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                >
                  <div className="text-right hidden sm:block">
                    <div className="text-sm font-medium text-foreground">{user?.name}</div>
                    <div className="text-xs text-muted-foreground">{user?.role}</div>
                  </div>
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} color="white" />
                  </div>
                  <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-md shadow-elevation-3 z-[1100]">
                    <div className="py-1">
                      <button
                        onClick={() => handleNavigation('/my-account')}
                        className="flex items-center w-full px-4 py-2 text-sm transition-colors hover:bg-muted text-popover-foreground"
                      >
                        <Icon name="User" size={16} className="mr-3" />
                        My Profile
                      </button>

                      <button
                        onClick={handleThemeToggle}
                        className="flex items-center w-full px-4 py-2 text-sm transition-colors hover:bg-muted text-popover-foreground"
                      >
                        <Icon name={isDark ? 'Sun' : 'Moon'} size={16} className="mr-3" />
                        {isDark ? 'Light' : 'Dark'} Mode
                      </button>
                      
                      <div className="border-t border-border my-1"></div>
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-destructive hover:bg-muted transition-colors"
                      >
                        <Icon name="LogOut" size={16} className="mr-3" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-t border-border px-6">
          <nav className="flex space-x-1 overflow-x-auto">
            {navigationSections?.map((section) => (
              <button
                key={section?.id}
                onClick={() => setActiveSection(section?.id)}
                className={`flex items-center px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                  activeSection === section?.id
                    ? 'border-primary text-primary bg-primary/5' :'border-transparent text-foreground hover:text-primary hover:border-primary/50'
                }`}
              >
                <Icon name={section?.icon} size={16} className="mr-2" />
                <span className="hidden sm:inline">{section?.label}</span>
                <span className="sm:hidden">{section?.label?.split(' ')?.[0]}</span>
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Content Area */}
      <main className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="mb-6">
            {navigationSections?.map((section) => (
              activeSection === section?.id && (
                <div key={section?.id} className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                    <Icon name={section?.icon} size={20} color="white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">{section?.label}</h2>
                    <p className="text-sm text-muted-foreground">{section?.description}</p>
                  </div>
                </div>
              )
            ))}
          </div>

          {/* Dynamic Content */}
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

export default AdminPortalDashboard;